import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, memo } from 'react';
import { CRM_OBJECT_TAG } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { PropertyContext } from '../context/PropertyContext';
import { ObjectContext } from '../context/ObjectContext';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import { usePropertyAccessLevel } from '../hooks/usePropertyAccessLevel';
import { usePropertyDefinition } from '../hooks/usePropertyDefinition';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { useObjectType } from '../hooks/useObjectType';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import FormattedPropertyCurrencyV2 from './FormattedPropertyCurrencyV2';
import FormattedPropertyPrice from './FormattedPropertyPrice';
import FormattedPropertyMonthPeriod from './FormattedPropertyMonthPeriod';
import FormattedPropertyHtml from './FormattedPropertyHtml';
import FormattedPropertyKnownGuid from './FormattedPropertyKnownGuid';
import FormattedPropertyPhone from './FormattedPropertyPhone';
import { isCurrency, isMultiCurrencyPrice, isHtml, isGuidPropertyWithKnownGuid, isMultienum, isPhoneNumber, isSubscriptionStatus, isPaymentStatus, isInvoiceStatus, isInvoiceDueDate, isPriority, isMonthPeriod } from 'customer-data-objects/property/PropertyIdentifier';
import PropertyInputHidden from 'customer-data-properties/input/PropertyInputHidden';
import FormattedInvoiceDueDate from 'customer-data-properties/formatted/FormattedInvoiceDueDate';
import FormattedInvoiceStatus from 'customer-data-properties/formatted/FormattedInvoiceStatus';
import FormattedSubscriptionStatus from 'customer-data-properties/formatted/FormattedSubscriptionStatus';
import FormattedPaymentStatus from 'customer-data-properties/formatted/FormattedPaymentStatus';
import { getDisplayedValue } from 'customer-data-property-utils/PropertyValueDisplay';
import { EMPTY_PROPERTY_VALUE } from '../../constants/EmptyPropertyValue';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { getDataAttributes } from '../../utils/getDataAttributes';
import { usePropertyComponentResolver } from '../hooks/usePropertyComponentResolver';
import ExternalOptionValue from '../../value/ExternalOptionValue';
import { useObjectId } from '../hooks/useObjectId';
import { MARKETING_REASON } from '../../resolvers/CustomResolverReferenceTypes';
import { stringifyReasonId } from '../../resolvers/custom/MarketingReasonReferenceResolver';
import FormattedCrmObjectTags from '../../formatted/FormattedCrmObjectTags';
import FormattedPriorityStatus from '../../formatted/FormattedPriorityStatus';
import { CONTACT_MARKETABLE_REASON_TYPE } from '../constants/AdditionalPropertyNames';

const FormattedPropertyV2 = props => {
  const {
    className,
    placeholder = EMPTY_PROPERTY_VALUE,
    simplifyCurrency,
    currencyProps,
    dateFormat,
    dateTimeFormat,
    relativeDate,
    relativeDateTime
  } = props;
  const accessLevel = usePropertyAccessLevel();
  const propertyDefinition = usePropertyDefinition();
  const propertyValue = usePropertyValue();
  const objectType = useObjectType();
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const resolver = usePropertyComponentResolver();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();

  if (accessLevel === HIDDEN) {
    return /*#__PURE__*/_jsx(PropertyInputHidden, Object.assign({
      className: className,
      property: propertyDefinition // We don't get a formControlUse prop in the formatter component so we have to
      // specify that we want the default behavior here. Usually this is set
      // in the top level PropertyInput component but we're skipping over it
      // here. This ensures we get a permissions icon and tooltip inline
      // with the value
      ,
      formControlUse: "default"
    }, getDataAttributes(props)));
  }

  if (isMultiCurrencyPrice(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyPrice, Object.assign({
      className: className,
      placeholder: placeholder,
      legacySimplifyCurrency: simplifyCurrency,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }

  if (isCurrency(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyCurrencyV2, Object.assign({
      className: className,
      placeholder: placeholder,
      legacySimplifyCurrency: simplifyCurrency,
      currencyProps: currencyProps
    }, getDataAttributes(props)));
  }

  if (isMonthPeriod(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyMonthPeriod, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }

  if (isHtml(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyHtml, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }

  if (isGuidPropertyWithKnownGuid(propertyDefinition, objectType, propertyValue)) {
    return /*#__PURE__*/_jsx(FormattedPropertyKnownGuid, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }

  if (isPhoneNumber(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPropertyPhone, Object.assign({
      className: className,
      placeholder: placeholder
    }, getDataAttributes(props)));
  }

  if (isSubscriptionStatus(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedSubscriptionStatus, {
      property: propertyDefinition,
      value: propertyValue
    });
  }

  if (isPaymentStatus(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedPaymentStatus, {
      property: propertyDefinition,
      value: propertyValue
    });
  }

  if (isInvoiceStatus(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedInvoiceStatus, {
      property: propertyDefinition,
      value: propertyValue
    });
  }

  if (isInvoiceDueDate(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(FormattedInvoiceDueDate, {
      property: propertyDefinition,
      value: propertyValue,
      dateFormat: dateFormat,
      dateTimeFormat: dateTimeFormat,
      relativeDate: relativeDate,
      relativeDateTime: relativeDateTime
    });
  }

  if (isPriority(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedPriorityStatus, {
      property: propertyDefinition,
      value: propertyValue
    });
  }

  if (resolver && resolver.referenceType === CRM_OBJECT_TAG) {
    return /*#__PURE__*/_jsx(FormattedCrmObjectTags, {
      resolver: resolver,
      value: propertyValue
    });
  }

  if (resolver) {
    let resolvableValue = propertyValue;

    if (resolver.referenceType === MARKETING_REASON) {
      // Marketing reasons resolve using the reason type+id instead of the value
      const marketableReasonType = getAdditionalPropertyValue(CONTACT_MARKETABLE_REASON_TYPE, objectId, objectTypeId);
      resolvableValue = marketableReasonType && propertyValue ? stringifyReasonId({
        reasonType: marketableReasonType,
        reasonSourceObjectId: propertyValue
      }) : propertyValue;
    }

    return /*#__PURE__*/_jsx(ExternalOptionValue, {
      multi: isMultienum(propertyDefinition),
      placeholder: placeholder,
      resolver: resolver,
      value: resolvableValue
    });
  }

  return /*#__PURE__*/_jsx("span", Object.assign({
    className: className
  }, getDataAttributes(props), {
    children: getDisplayedValue(propertyDefinition, propertyValue, undefined, {
      dateFormat,
      dateTimeFormat,
      relativeDate,
      relativeDateTime
    }) || placeholder
  }));
};

FormattedPropertyV2.displayName = 'FormattedPropertyV2';

const FormattedPropertyV2WithContexts = (_ref) => {
  let {
    accessLevel: accessLevelOverride,
    getAdditionalPropertyValue,
    objectId,
    objectTypeId,
    property,
    value
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["accessLevel", "getAdditionalPropertyValue", "objectId", "objectTypeId", "property", "value"]);

  const {
    getIsUngated
  } = rest;
  const propertyContextValue = useMemo(() => ({
    accessLevelOverride,
    objectTypeId,
    property,
    value,
    getIsUngated
  }), [accessLevelOverride, objectTypeId, property, value, getIsUngated]);
  const objectContextValue = useMemo(() => ({
    getAdditionalPropertyValue,
    objectId
  }), [getAdditionalPropertyValue, objectId]);
  return /*#__PURE__*/_jsx(ObjectContext.Provider, {
    value: objectContextValue,
    children: /*#__PURE__*/_jsx(PropertyContext.Provider, {
      value: propertyContextValue,
      children: /*#__PURE__*/_jsx(FormattedPropertyV2, Object.assign({}, rest))
    })
  });
};

export default /*#__PURE__*/memo(FormattedPropertyV2WithContexts);